/**
 * Read receipts, derived rather than stored.
 *
 * A leaf module: no React, no Supabase, no app-mode. It replaces `messages.read`,
 * a boolean whose only writer targeted `sender_id <> auth.uid()` while the sole
 * UPDATE policy on the table required `sender_id = auth.uid()`. The two could
 * never both hold, so the flag was never set for anyone and the double tick in
 * RealtimeChat was unreachable code — every message rendered a single grey tick
 * from the day the feature was written.
 *
 * The inputs now come from real read state:
 *   - `counterpartyLastReadAt` — the server-maintained per-SIDE mark on
 *     `conversations`, which answers "has anyone over there caught up".
 *   - `counterpartyMarks` — per-person marks from
 *     `read_marks_for_conversation()`, which answers "who".
 *
 * The side mark is authoritative for the READ/SENT decision and the per-person
 * marks only supply names, so a receipt never regresses to "Sent" while the names
 * are still in flight.
 */

export type CounterpartyMark = {
  /** Resolved server-side from `profiles`; null when the person has no name set. */
  displayName: string | null;
  lastReadAt: string;
};

export type ReadReceipt =
  | { state: 'sent' }
  /** `by` may be empty: the side mark can say "read" before the names arrive. */
  | { state: 'read'; by: string[] };

export function resolveReadReceipt(args: {
  messageCreatedAt: string;
  counterpartyLastReadAt: string | null;
  counterpartyMarks: CounterpartyMark[];
}): ReadReceipt {
  const { messageCreatedAt, counterpartyLastReadAt, counterpartyMarks } = args;

  if (!counterpartyLastReadAt) return { state: 'sent' };

  const sentAt = new Date(messageCreatedAt).getTime();
  if (new Date(counterpartyLastReadAt).getTime() < sentAt) return { state: 'sent' };

  // Only the people whose OWN read covers this message. A teammate who last
  // looked an hour before it arrived has not seen it, and naming them would be a
  // lie that the side-level mark alone cannot distinguish.
  const by = counterpartyMarks
    .filter((m) => new Date(m.lastReadAt).getTime() >= sentAt)
    .map((m) => m.displayName)
    .filter((name): name is string => !!name && name.trim().length > 0);

  return { state: 'read', by };
}

/**
 * The label.
 *
 * `counterpartyIsTeam` is known without asking the server: a conversation joins a
 * startup TEAM to exactly one investor profile. So an investor is looking at a
 * team, where "Read by Maude" is actionable, and a founder is looking at one
 * person, where naming them only restates "Read".
 */
export function formatReadReceipt(
  receipt: ReadReceipt,
  { counterpartyIsTeam }: { counterpartyIsTeam: boolean }
): string {
  if (receipt.state === 'sent') return 'Sent';
  if (!counterpartyIsTeam || receipt.by.length === 0) return 'Read';

  // First name only — the surname adds width to a 12px caption without adding
  // information, and the thread's own bylines already carry full names.
  const [first, ...rest] = receipt.by;
  const firstName = first.trim().split(/\s+/)[0];
  return rest.length > 0 ? `Read by ${firstName} +${rest.length}` : `Read by ${firstName}`;
}
